import React, { useState } from 'react'
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import { Square } from '../../../utility/square/Square'

interface BarChartData {
  name: string
  color: string
  data: { time: string; value: number }[]
}

interface AiBarChartProps {
  title: string
  subtitle: string
  data?: BarChartData[]
}

export const AiBarChart: React.FC<AiBarChartProps> = ({ title, subtitle, data = [] }) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const totalValues = data.map((serie) => serie.data.reduce((sum, item) => sum + item.value, 0))

  const mergedData = data[0]?.data.map((_, idx) => {
    const row: any = { time: data[0].data[idx].time }
    data.forEach((serie) => {
      row[serie.name] = serie.data[idx].value
    })
    return row
  })

  return (
    <Square className='w-full max-w-[336px] py-[12px] px-[24px]'>
      <div className='mb-4 flex flex-col'>
        <div className='text-[20px] text-focus-500'>{title}</div>
        <div className='text-[14px] opacity-50'>{subtitle}</div>
      </div>

      <ResponsiveContainer height={150} width='100%'>
        <RechartsBarChart data={mergedData} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
          <CartesianGrid strokeDasharray='3 3' />
          <XAxis axisLine={false} dataKey='time' tickLine={false} />
          <YAxis axisLine={false} tickLine={true} width={20} />

          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                return (
                  <div className='flex min-h-[24px] flex-col gap-1 rounded-[12px] bg-focus-500 py-[8px] px-[12px] text-[14px] font-medium leading-[20px]'>
                    {payload.map((item, index) => (
                      <div key={index} className='flex justify-between gap-[12px]'>
                        <span className='text-white'>{item.name}</span>
                        <span className='text-aqua-300'>{item.value}</span>
                      </div>
                    ))}
                  </div>
                )
              }
              return null
            }}
          />

          {data.map((serie, idx) => (
            <Bar
              key={idx}
              dataKey={serie.name}
              fill={serie.color}
              fillOpacity={activeIndex === null || activeIndex === idx ? 1 : 0}
              stroke={serie.color}
              strokeOpacity={activeIndex === null || activeIndex === idx ? 1 : 0}
              type='monotone'
            />
          ))}
        </RechartsBarChart>
      </ResponsiveContainer>

      <div className='mt-4 flex flex-col gap-2'>
        {data.map((serie, idx) => (
          <div
            key={idx}
            className='flex cursor-pointer items-center justify-between gap-2 transition-opacity'
            style={{ opacity: activeIndex === null || activeIndex === idx ? 1 : 0.6 }}
            onMouseEnter={() => setActiveIndex(idx)}
            onMouseLeave={() => setActiveIndex(null)}
          >
            <div className='flex items-center gap-[8px]'>
              <div
                className='h-[10px] w-[10px] rounded-full'
                style={{ backgroundColor: serie.color }}
              />
              <span className='text-sm text-gray-700'>{serie.name}</span>
            </div>
            <span className='text-sm text-gray-500'>{totalValues[idx]}</span>
          </div>
        ))}
      </div>
    </Square>
  )
}
